/**
 * @fileoverview Generic modal for status change with justification
 * Following DMS component patterns from the framework
 */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, DmsSelect, DmsTextField, intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import { Box, Typography, Chip, Alert, useMediaQuery, useTheme } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";
import { DmsElectronicSignature } from "../DmsElectronicSignature";

/**
 * Generic modal for changing entity status with justification
 *
 * @example
 * <DmsStatusChangeModal
 *   open={showModal}
 *   onClose={handleClose}
 *   entityPk={patient.pk}
 *   entityName={patient.name}
 *   currentStatus="ACTIVE"
 *   newStatus="INACTIVE"
 *   apiEndpoint="/patient"
 *   buildPayload={(reason) => ({ status: "INACTIVE", status_reason: reason })}
 *   onSuccess={handleSuccess}
 *   title="Change Patient Status"
 *   statusLabels={{ ACTIVE: "Active", INACTIVE: "Inactive" }}
 * />
 */
export const DmsStatusChangeModal = ({
  open,
  onClose,
  entityPk,
  entityName = null,
  currentStatus,
  newStatus,
  apiEndpoint,
  apiMethod = "PUT", // HTTP method: "PUT" (default), "POST", etc.
  apiPathSuffix = "", // Optional path suffix: "" (default) or "/block", "/unblock", etc.
  buildPayload,
  onSuccess = null,
  title = null,
  statusLabels = null,
  reasonLabel = null,
  reasonRequired = false,
  reasonRows = 3,
  confirmBeforeSave = false,
  confirmMessage = null,
  reasonType = null, // "patient_status_reason" or "prescription_reason" - if provided, use dropdown
  warningMessage = null, // Warning message to display in the modal
}) => {
  const [reason, setReason] = useState("");
  const [reasonPk, setReasonPk] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [saving, setSaving] = useState(false);

  // Electronic signature for active prescription confirmation
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [pendingForceAction, setPendingForceAction] = useState(null);

  // Fetch reason options if reasonType is provided
  useEffect(() => {
    if (reasonType && open) {
      setLoadingReasons(true);
      fetchData(`/${reasonType}/all`, 'GET', true)
        .then(response => {
          // Handle different response formats from GenericRouter
          const data = response?.data?.data?.rows || response?.data?.data || response?.data?.rows || response?.data || [];
          setReasonOptions(Array.isArray(data) ? data : []);
        })
        .catch(error => {
          console.error('Error loading reasons:', error);
          setReasonOptions([]);
        })
        .finally(() => {
          setLoadingReasons(false);
        });
    }
  }, [reasonType, open]);

  // Check if selected reason is "Other" — detect by reason name
  const isOtherSelected = useMemo(() => {
    if (!reasonPk) return false;
    const selected = reasonOptions.find(r => r.pk === reasonPk);
    return selected?.name?.toUpperCase() === "OTHER";
  }, [reasonPk, reasonOptions]);

  const handleClose = useCallback(() => {
    setReason("");
    setReasonPk("");
    setCustomReason("");
    onClose();
  }, [onClose]);

  // Execute save with optional force flag
  const executeSave = useCallback(async (forceInactivation = false) => {
    setSaving(true);

    try {
      // Build payload based on reasonType
      let payload;
      if (reasonType) {
        // Use dropdown: send reasonPk, customReason, newStatus, and reasonOptions for lookup
        payload = buildPayload(reasonPk, customReason.trim() || null, newStatus, reasonOptions);
      } else {
        // Use text field: send reason text
        payload = buildPayload(reason.trim(), newStatus);
      }

      const method = apiMethod || "PUT";

      // Build URL and handle force flag based on HTTP method
      let url = `${apiEndpoint}/${entityPk}${apiPathSuffix || ""}`;

      if (forceInactivation) {
        if (method === "DELETE") {
          // For DELETE: use query parameter (e.g., doctor_patient endpoint)
          url += "?force=true";
        } else {
          // For PUT/POST: add to payload (e.g., patient toggle_status endpoint)
          payload = { ...payload, force_inactivation: true };
        }
      }

      const response = await fetchData(
        url,
        method,
        true,
        payload,
        false,
        false // Disable default toast to handle errors manually
      );

      if (response.status === HTTP_STATUS.OK_200) {
        setReason("");
        setReasonPk("");
        setCustomReason("");
        setPendingForceAction(null);
        onSuccess?.(response.data?.data);
        onClose();
      } else {
        // Handle specific error types
        const errorDetail = response.data?.detail;

        // Check for active_medication_cycle error - show warning with cycle info
        if (typeof errorDetail === 'object' && errorDetail?.type === 'active_medication_cycle') {
          const daysRemaining = errorDetail.days_remaining || 0;
          const dosageDuration = errorDetail.dosage_duration;
          const isDispensed = dosageDuration != null;

          let swalConfig;

          const formatDate = (isoStr) => {
            if (!isoStr) return '-';
            const d = new Date(isoStr);
            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
          };

          if (isDispensed) {
            // Case: Dispensed - patient is currently taking medication
            const dispensationDate = formatDate(errorDetail.dispensation_date);
            const prescriptionExpirationDate = formatDate(errorDetail.prescription_expiration_date);

            swalConfig = {
              title: intl.get("active_medication_cycle_title").d("Active Medication Cycle"),
              html: `
                <div style="text-align: left;">
                  <p style="margin-bottom: 8px;">
                    ${intl.get("active_medication_cycle_message").d("This patient is currently taking medication.")}
                  </p>
                  <div style="background: #fff3cd; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
                    <p style="margin: 0; color: #856404;">
                      <strong>${intl.get("patient_still_on_medication").d("Patient still on medication")}</strong>
                    </p>
                    <p style="margin: 8px 0 0 0; color: #856404;">
                      ${intl.get("active_medication_cycle_warning")
                        .d("The patient received medication on {dispensationDate} and is still in a {totalDays}-day cycle.")
                        .replace('{dispensationDate}', dispensationDate)
                        .replace('{totalDays}', dosageDuration)}
                    </p>
                  </div>
                  <p style="margin: 0;">
                    <strong>${intl.get("prescription_expiration_date").d("Prescription expiration date")}:</strong> ${prescriptionExpirationDate}
                  </p>
                  <p style="margin: 8px 0 16px 0;">
                    <strong>${intl.get("days_remaining_in_cycle").d("Days remaining in cycle")}:</strong> ${daysRemaining}
                  </p>
                </div>
              `,
            };
          } else {
            // Case: Active prescription, not yet dispensed
            const prescriptionExpirationDate = formatDate(errorDetail.cycle_end_date);

            swalConfig = {
              title: intl.get("active_prescription_not_dispensed_title").d("Active Prescription"),
              html: `
                <div style="text-align: left;">
                  <p style="margin-bottom: 16px;">
                    ${intl.get("active_prescription_not_dispensed_message").d("This patient currently has an active and undispensed prescription.")}
                  </p>
                  <div style="background: #fff3cd; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
                    <p style="margin: 0; color: #856404;">
                      <strong>${intl.get("prescription_expiration_date").d("Prescription expiration date")}:</strong> ${prescriptionExpirationDate}
                    </p>
                    <p style="margin: 8px 0 0 0; color: #856404;">
                      <strong>${intl.get("days_until_prescription_expires").d("Days until prescription expires")}:</strong> ${daysRemaining}
                    </p>
                  </div>
                  <p style="margin: 0; color: #666;">
                    ${intl.get("confirm_inactivate_open_prescription").d("Are you sure you want to inactivate this patient even though they have an open prescriptive cycle?")}
                  </p>
                </div>
              `,
            };
          }

          const result = await Swal.fire({
            ...swalConfig,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: intl.get("yes_inactivate").d("Yes, inactivate"),
            cancelButtonText: intl.get("Cancel").d("Cancel"),
            confirmButtonColor: '#d33',
          });

          if (result.isConfirmed) {
            await executeSave(true);
          }

          return;
        }

        // Check for active_prescription error - requires electronic signature to force
        if (typeof errorDetail === 'object' && errorDetail?.type === 'active_prescription') {
          // Store the action to retry after signature confirmation
          setPendingForceAction({ payload });

          // Show warning first
          await Swal.fire({
            icon: "warning",
            title: intl.get("active_prescription_title").d("Active Prescriptions"),
            html: `
              <div style="text-align: left;">
                <p>${intl.get("active_prescription_message").d("This patient has active prescriptions. Inactivating them will cancel all active prescriptions.")}</p>
                <p style="margin-top: 12px; color: #666;">
                  ${intl.get("electronic_signature_required").d("To proceed with this action, you need to confirm with your electronic signature.")}
                </p>
              </div>
            `,
            confirmButtonText: intl.get("proceed_to_signature").d("Proceed to Signature"),
            showCancelButton: true,
            cancelButtonText: intl.get("Cancel").d("Cancel"),
          }).then((result) => {
            if (result.isConfirmed) {
              setShowSignatureModal(true);
            } else {
              setPendingForceAction(null);
            }
          });

          return;
        }

        // Generic error handling
        const errorMessage = typeof errorDetail === 'string'
          ? errorDetail
          : errorDetail?.message || intl.get("StatusChangeError").d("Error changing status");

        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: errorMessage,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: error.message || intl.get("StatusChangeError").d("Error changing status"),
      });
    } finally {
      setSaving(false);
    }
  }, [
    reason,
    reasonPk,
    customReason,
    reasonType,
    buildPayload,
    newStatus,
    apiEndpoint,
    apiMethod,
    apiPathSuffix,
    entityPk,
    onSuccess,
    onClose,
    reasonOptions,
  ]);

  // Handle electronic signature success - retry with force
  const handleSignatureSuccess = useCallback(() => {
    setShowSignatureModal(false);
    if (pendingForceAction) {
      executeSave(true); // Execute with force=true
    }
  }, [pendingForceAction, executeSave]);

  // Handle electronic signature close/cancel
  const handleSignatureClose = useCallback(() => {
    setShowSignatureModal(false);
    setPendingForceAction(null);
  }, []);

  const handleSave = useCallback(async () => {
    // Validate reason selection
    if (reasonType) {
      if (reasonRequired && !reasonPk) {
        Swal.fire({
          icon: "warning",
          title: intl.get("MissingInformation").d("Missing Information"),
          text: intl.get("ReasonRequired").d("Reason Required"),
        });
        return;
      }
      // If "Other" is selected, require custom reason
      if (isOtherSelected && (!customReason || !customReason.trim())) {
        Swal.fire({
          icon: "warning",
          title: intl.get("MissingInformation").d("Missing Information"),
          text: intl.get("CustomReasonRequired").d("Please provide a custom reason."),
        });
        return;
      }
    } else {
      // Original text field validation
      if (reasonRequired && !reason.trim()) {
        Swal.fire({
          icon: "warning",
          title: intl.get("MissingInformation").d("Missing Information"),
          text: intl.get("ReasonRequired").d("Reason Required"),
        });
        return;
      }
    }

    if (confirmBeforeSave) {
      const result = await Swal.fire({
        title: intl.get("ConfirmStatusChange").d("Confirm Status Change"),
        text: confirmMessage || intl.get("ConfirmStatusChangeMessage").d("Are you sure you want to change the status?"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: intl.get("Confirm").d("Confirm"),
        cancelButtonText: intl.get("Cancel").d("Cancel"),
      });

      if (!result.isConfirmed) return;
    }

    // Execute save (without force initially)
    await executeSave(false);
  }, [
    reason,
    reasonPk,
    customReason,
    reasonRequired,
    reasonType,
    isOtherSelected,
    confirmBeforeSave,
    confirmMessage,
    executeSave,
  ]);

  const getStatusLabel = (status) => {
    if (statusLabels && statusLabels[status]) {
      return statusLabels[status];
    }
    return status;
  };

  const getStatusColor = (status) => {
    const upperStatus = String(status).toUpperCase();
    if (upperStatus === "ACTIVE" || upperStatus === "1" || status === 1) {
      return "success";
    }
    if (upperStatus === "INACTIVE" || upperStatus === "0" || status === 0) {
      return "error";
    }
    return "default";
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
    <DmsModal
      isVisible={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DmsModal.Header
        title={title || intl.get("StatusChange").d("Status Change")}
        onClose={handleClose}
      />
      <DmsModal.Body>
        <Box sx={{ mb: 3, p: isMobile ? 1 : 2 }}>
          {entityName && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>{entityName}</strong>
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                {intl.get("CurrentStatus").d("Current Status")}
              </Typography>
              <Chip
                label={getStatusLabel(currentStatus)}
                color={getStatusColor(currentStatus)}
                size="small"
              />
            </Box>
            <Typography variant="h6" color="text.secondary">→</Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {intl.get("NewStatus").d("New Status")}
              </Typography>
              <Chip
                label={getStatusLabel(newStatus)}
                color={getStatusColor(newStatus)}
                size="small"
              />
            </Box>
          </Box>

          {warningMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warningMessage}
            </Alert>
          )}

          {reasonType ? (
            <>
              <DmsSelect
                key={`reason-select-${reasonOptions.length}`}
                label={reasonLabel || intl.get("StatusReason").d("Status Reason")}
                value={reasonPk}
                onChange={(value) => {
                  setReasonPk(value);
                  if (!isOtherSelected) {
                    setCustomReason("");
                  }
                }}
                options={reasonOptions.map(r => ({
                  value: r.pk,
                  label: intl.get(r.name).d(r.name)
                }))}
                fullWidth
                required={reasonRequired}
                disabled={loadingReasons}
                sx={{ mb: isOtherSelected ? 2 : 0 }}
              />
              {isOtherSelected && (
                <DmsTextField
                  label={intl.get("CustomReason").d("Custom Reason")}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={reasonRows || 3}
                  required
                  placeholder={intl.get("EnterCustomReasonPlaceholder").d("Enter the custom reason...")}
                  sx={{ mt: 2 }}
                />
              )}
            </>
          ) : (
            <DmsTextField
              label={reasonLabel || intl.get("StatusReason").d("Status Reason")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={reasonRows}
              required={reasonRequired}
              placeholder={intl.get("EnterReasonPlaceholder").d("Enter the reason for this status change...")}
            />
          )}
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 1,
            width: "100%",
            p: isMobile ? 1 : 2,
          }}
        >
          <DmsButton
            icon={<CancelIcon />}
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleClose}
            disabled={saving}
            fullWidth={isMobile}
          />
          <DmsButton
            icon={<SaveIcon />}
            text={saving ? intl.get("Saving").d("Saving...") : intl.get("Confirm").d("Confirm")}
            onClick={handleSave}
            disabled={saving || (reasonType && reasonRequired && !reasonPk) || (reasonType && isOtherSelected && !customReason?.trim())}
            fullWidth={isMobile}
          />
        </Box>
      </DmsModal.Footer>
    </DmsModal>

    {/* Electronic Signature Modal for force inactivation with active prescriptions */}
    <DmsElectronicSignature
      show={showSignatureModal}
      setShow={setShowSignatureModal}
      onSuccess={handleSignatureSuccess}
      onClose={handleSignatureClose}
      title={intl.get("confirm_patient_inactivation").d("Confirm Patient Inactivation")}
      actionDescription={intl.get("inactivate_patient_with_prescriptions").d("Inactivate patient with active prescriptions - this will cancel all active prescriptions")}
      logModule="patient/force_inactivation"
    />
    </>
  );
};

DmsStatusChangeModal.propTypes = {
  /** Modal visibility */
  open: PropTypes.bool.isRequired,
  /** Close handler */
  onClose: PropTypes.func.isRequired,
  /** Entity primary key */
  entityPk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Entity name to display */
  entityName: PropTypes.string,
  /** Current status value */
  currentStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** New status value */
  newStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** API endpoint (without pk) - e.g., "/patient" */
  apiEndpoint: PropTypes.string.isRequired,
  /** HTTP method for API call - default "PUT" */
  apiMethod: PropTypes.string,
  /** Path suffix after entity pk - e.g., "/block" for "/user_management/{pk}/block" */
  apiPathSuffix: PropTypes.string,
  /**
   * Function to build the update payload
   * - Without reasonType: (reason, newStatus) => payload
   * - With reasonType: (reasonPk, customReason, newStatus, reasonOptions) => payload
   */
  buildPayload: PropTypes.func.isRequired,
  /** Success callback - receives updated data */
  onSuccess: PropTypes.func,
  /** Modal title */
  title: PropTypes.string,
  /** Map of status values to display labels */
  statusLabels: PropTypes.object,
  /** Label for reason field */
  reasonLabel: PropTypes.string,
  /** Whether reason is required */
  reasonRequired: PropTypes.bool,
  /** Number of rows for reason text field */
  reasonRows: PropTypes.number,
  /** Whether to show confirmation dialog before save */
  confirmBeforeSave: PropTypes.bool,
  /** Custom confirmation message */
  confirmMessage: PropTypes.string,
  /** Reason type for dropdown: "patient_status_reason" or "prescription_reason" */
  reasonType: PropTypes.string,
  /** Warning message to display in the modal */
  warningMessage: PropTypes.string,
};

export default DmsStatusChangeModal;
